<template lang="pug">
.screen-bank
  uc-scroll-section(
    :data-bank-id="screen.id"
    :id="`${screen.id}-section${i}`"
    v-for="(s, i) in questionsToShow"
  )
    .wrapper
      audio-marker(:name="s.id")
      uc-screen(
        :editPath="`${editPath}.bank.${s.screenIndex}`"
        :key="s.id" :screen="s"
      )
</template>

<script setup lang="ts">
import {
  computed,
  inject,
  unref,
  onMounted,
  ref,
  nextTick,
  watch,
  withDefaults,
  type Ref
} from 'vue';
import { shuffle } from '../lib/shuffle';
import { set } from '../lib/SCORM';
import useSuspend from '../lib/useSuspend';
import { clearQueue, play, stop } from '../lib/audio';
import {
  useChildScreenAudio,
  type NarratableScreen
} from '../lib/useScreenAudio';

const audioDoc = inject('audioDoc') as boolean;
const wordDoc = unref<boolean>(inject('wordDoc'));
const suspend = useSuspend();

const props = withDefaults(
  defineProps<{
    editPath: string;
    screen: UcScreenRequiredId & {
      count?: number;
      'shuffle-questions'?: boolean;
      'for-quiz'?: string;
    };
    /** Ancestor owns section intro when nested under `uc-screen` / `templates[]`. */
    nested?: boolean;
  }>(),
  { nested: false }
);

const allQuestions = props.screen.bank;
const shuffleQuestions = props.screen['shuffle-questions'] ?? true;

const count = computed(() =>
  props.screen.count && props.screen.count < allQuestions.length
    ? props.screen.count
    : allQuestions.length
);

const questionsToShow = computed(() => {
  if (wordDoc || audioDoc) {
    return allQuestions;
  }

  let questionIds: string[] = [];

  // Check if we have stored a question order for this bank
  if (suspend.bankQuestions && suspend.bankQuestions[props.screen.id]) {
    questionIds = JSON.parse(suspend.bankQuestions[props.screen.id]);
  } else {
    questionIds = createAndSaveQuestionOrder();
  }

  const activeQuestions = questionIds.map(id =>
    allQuestions.find(q => q.id === id)
  );

  console.log(
    `Bank: ${props.screen.id}, shuffleQuestions: ${shuffleQuestions}, questionIds: ${questionIds}`
  );

  return activeQuestions;
});

const createAndSaveQuestionOrder = () => {
  const compulsoryQuestions = shuffleQuestions
    ? shuffle(allQuestions?.filter(q => q.compulsory) ?? [])
    : (allQuestions?.filter(q => q.compulsory) ?? []);
  const optionalQuestions = shuffleQuestions
    ? shuffle(allQuestions?.filter(q => !q.compulsory) ?? [])
    : (allQuestions?.filter(q => !q.compulsory) ?? []);
  const remainingSlots = count.value - (compulsoryQuestions?.length ?? 0);

  let selectedQuestions = [
    ...compulsoryQuestions,
    ...optionalQuestions.slice(0, remainingSlots)
  ];

  const filteredAndShuffled = shuffleQuestions
    ? shuffle(selectedQuestions).slice(0, count.value)
    : selectedQuestions.slice(0, count.value);

  const questionIds = filteredAndShuffled.map(screen => screen.id);

  // Store the question order for this bank
  set(`bankQuestions.${props.screen.id}`, JSON.stringify(questionIds));

  return questionIds;
};

// First-question audio on initial bank section entry — same pattern as
// screen-quiz.vue. See screen-bank.schema.ts for the full explanation.
const sectionActive = inject<Ref<boolean>>('sectionActive', ref(false));
const { resolveChildIntro } = useChildScreenAudio();

const playFirstQuestionIntro = async () => {
  const first = questionsToShow.value?.[0] as NarratableScreen | undefined;
  if (!first?.id) return;

  clearQueue();
  stop();

  const audio = await resolveChildIntro(first);
  if (audio) await play(audio, { owner: first.id });
};

watch(sectionActive, async active => {
  if (props.nested) return;
  if (!active) return;
  await playFirstQuestionIntro();
});

onMounted(async () => {
  await nextTick();
  if (props.nested) return;
  if (!sectionActive.value) return;
  await playFirstQuestionIntro();
});
</script>

<style>
.screen-bank + .screen-next-button {
  display: none;
}
</style>
